import { fetchWithTimeout } from './deco-http.js';
import {
  getPackAddDecision,
  getPackRulesConfig,
  getViolationMessage,
  mapSkuToFurniture,
  validatePackSelection,
} from '@theme/pack-rules';
import {
  clearDecoAccountSession,
  DECO_ACCOUNT_CHANGED_EVENT,
  dispatchDecoAccountChanged,
  getDecoAccountSessionToken,
  hasDecoAccountSession,
  readDecoAccountProfile,
  readDecoAccountSubscriptions,
  writeDecoAccountSession,
} from '@theme/deco-account-session';

const GOOGLE_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const PRODUCT_IMAGE_CACHE_STORAGE_KEY = 'deco_account_product_images';
const PRODUCT_IMAGE_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const PACK_QUOTE_ENDPOINTS = [
  'https://api.decorental.com.ar/v1/packs/quote',
  'https://api.decorental.com.ar/v1/packs/calculate',
  'https://api.decorental.com.ar/v1/packs/quotation',
];
const TERMS_MODAL_ID = 'deco-terms-modal';
const PACK_PICKER_MODAL_ID = 'deco-pack-picker-modal';
const PACK_CHANGE_CONFIRM_MODAL_ID = 'deco-pack-change-confirm-modal';
const PACK_CHANGE_OPEN_STATUSES = new Set([
  'PENDING',
  'APPROVED',
  'APPLYING',
  'REVIEW_REQUIRED',
  'RECONCILIATION_REQUIRED',
]);
const CONTACT_FIELDS = ['telefono', 'cuil', 'direccion', 'direccion_numero', 'direccion_piso', 'direccion_dpto', 'codigo_postal', 'ciudad'];
const REQUIRED_CONTACT_FIELDS = ['telefono', 'cuil', 'direccion', 'direccion_numero', 'codigo_postal', 'ciudad'];
const PROFILE_TABS = ['usuario', 'personales', 'suscripciones', 'terminos'];
const FIELD_AUTOCOMPLETE = {
  nombre: 'given-name',
  apellido: 'family-name',
  email: 'email',
  telefono: 'tel',
  cuil: 'off',
  direccion: 'address-line1',
  direccion_numero: 'address-line2',
  direccion_piso: 'address-line2',
  direccion_dpto: 'address-line2',
  codigo_postal: 'postal-code',
  ciudad: 'address-level2',
};

let googleScriptPromise = null;
let googleClientId = null;
let decoAccountInstanceCount = 0;
const productImageRequests = new Map();
const subscriptionPaymentRequests = new Map();

function loadGoogleScript() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (googleScriptPromise) return googleScriptPromise;

  googleScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GOOGLE_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('No se pudo cargar Google.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = GOOGLE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('No se pudo cargar Google.'));
    document.head.appendChild(script);
  });

  return googleScriptPromise;
}

function initializeGoogle(clientId) {
  if (!window.google?.accounts?.id || !clientId) return;

  if (googleClientId === clientId) return;

  googleClientId = clientId;
  window.google.accounts.id.initialize({
    client_id: clientId,
    callback: (response) => {
      window.dispatchEvent(new CustomEvent('deco-account:google-credential', {
        detail: { credential: response?.credential || '' },
      }));
    },
  });
}

function decodeJwtPayload(token) {
  const payload = String(token || '').split('.')[1];
  if (!payload) return {};

  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
  const binary = window.atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function normalizeSku(value) {
  return String(value || '').trim().toUpperCase();
}

function normalizeImageUrl(value) {
  const rawValue = typeof value === 'string' ? value : (value?.url || value?.src || value?.original_src || '');
  if (!rawValue) return '';

  try {
    return new URL(rawValue, window.location.origin).href;
  } catch (_error) {
    return '';
  }
}

function normalizePaymentUrl(value) {
  const rawValue = typeof value === 'string' ? value.trim() : '';
  if (!rawValue) return '';

  try {
    const url = new URL(rawValue, window.location.origin);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
  } catch (_error) {
    return '';
  }
}

function normalizeQuotePaymentUrl(payload) {
  if (!payload || typeof payload !== 'object') return '';

  const nestedQuote = typeof payload.quote === 'object' ? payload.quote : null;
  const nestedDataQuote = typeof payload.data?.quote === 'object' ? payload.data.quote : null;
  const candidates = [
    payload.payment_url,
    payload.mp_checkout_url,
    payload.mp_url,
    payload.mp_url_used,
    payload.checkout_url,
    payload.pack?.payment_url,
    payload.pack?.mp_checkout_url,
    nestedQuote?.payment_url,
    nestedQuote?.mp_checkout_url,
    nestedQuote?.pack?.payment_url,
    nestedQuote?.pack?.mp_checkout_url,
    payload.data?.payment_url,
    payload.data?.mp_checkout_url,
    payload.data?.pack?.payment_url,
    payload.data?.pack?.mp_checkout_url,
    nestedDataQuote?.payment_url,
    nestedDataQuote?.mp_checkout_url,
    nestedDataQuote?.pack?.payment_url,
    nestedDataQuote?.pack?.mp_checkout_url,
  ];

  for (const candidate of candidates) {
    const paymentUrl = normalizePaymentUrl(candidate);
    if (paymentUrl) return paymentUrl;
  }

  return '';
}

function readProductImageCache() {
  try {
    const cached = JSON.parse(sessionStorage.getItem(PRODUCT_IMAGE_CACHE_STORAGE_KEY) || '{}');
    return cached && typeof cached === 'object' ? cached : {};
  } catch (_error) {
    return {};
  }
}

function writeProductImageCache(cache) {
  try {
    sessionStorage.setItem(PRODUCT_IMAGE_CACHE_STORAGE_KEY, JSON.stringify(cache));
  } catch (_error) {
    // ignore cache write errors
  }
}

function getCachedProductImage(sku) {
  const normalizedSku = normalizeSku(sku);
  if (!normalizedSku) return '';

  const cache = readProductImageCache();
  const entry = cache[normalizedSku];
  if (!entry || Date.now() - Number(entry.updatedAt || 0) > PRODUCT_IMAGE_CACHE_TTL_MS) {
    delete cache[normalizedSku];
    writeProductImageCache(cache);
    return '';
  }
  return normalizeImageUrl(entry.url);
}

function setCachedProductImage(sku, url) {
  const normalizedSku = normalizeSku(sku);
  if (!normalizedSku) return;

  const cache = readProductImageCache();
  cache[normalizedSku] = {
    url: normalizeImageUrl(url),
    updatedAt: Date.now(),
  };
  writeProductImageCache(cache);
}

function findProductImageInDom(sku) {
  const normalizedSku = normalizeSku(sku);
  if (!normalizedSku) return '';

  const card = Array.from(document.querySelectorAll('product-card[data-product-sku]'))
    .find((element) => normalizeSku(element.dataset.productSku) === normalizedSku);
  if (!(card instanceof HTMLElement)) return '';

  const featuredMediaUrl = card.closest('product-card-link')?.dataset?.featuredMediaUrl || '';
  const fromFeaturedMedia = normalizeImageUrl(featuredMediaUrl);
  if (fromFeaturedMedia) return fromFeaturedMedia;

  const image = card.querySelector('img');
  return normalizeImageUrl(image?.currentSrc || image?.src || '');
}

function getPredictiveProductImage(product) {
  const candidates = [
    product?.image,
    product?.featured_image,
    product?.featuredImage,
    product?.featured_media?.preview_image,
    product?.featured_media?.image,
    product?.images?.[0],
  ];

  for (const candidate of candidates) {
    const imageUrl = normalizeImageUrl(candidate);
    if (imageUrl) return imageUrl;
  }
  return '';
}

async function fetchProductImageFromSearch(query) {
  const cleanQuery = String(query || '').trim();
  if (!cleanQuery) return '';

  const url = new URL('/search/suggest.json', window.location.origin);
  url.searchParams.set('q', cleanQuery);
  url.searchParams.set('resources[type]', 'product');
  url.searchParams.set('resources[limit]', '5');
  url.searchParams.set('resources[options][unavailable_products]', 'show');

  const response = await fetchWithTimeout(url.toString(), {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) return '';

  const data = await response.json();
  const products = data?.resources?.results?.products || [];
  if (!Array.isArray(products)) return '';

  for (const product of products) {
    const imageUrl = getPredictiveProductImage(product);
    if (imageUrl) return imageUrl;
  }
  return '';
}

async function resolveSubscriptionItemImage(item) {
  const sku = normalizeSku(item?.sku);
  if (!sku) return '';

  const explicitImage = normalizeImageUrl(item?.image_url);
  if (explicitImage) return explicitImage;

  const domImage = findProductImageInDom(sku);
  if (domImage) {
    setCachedProductImage(sku, domImage);
    return domImage;
  }

  const cachedImage = getCachedProductImage(sku);
  if (cachedImage) return cachedImage;

  if (productImageRequests.has(sku)) {
    return productImageRequests.get(sku);
  }

  const request = (async () => {
    const bySku = await fetchProductImageFromSearch(sku);
    const imageUrl = bySku || await fetchProductImageFromSearch(item?.nombre || '');
    setCachedProductImage(sku, imageUrl);
    return imageUrl;
  })().finally(() => {
    productImageRequests.delete(sku);
  });

  productImageRequests.set(sku, request);
  return request;
}

function readStoredProfile() {
  return readDecoAccountProfile();
}

function readStoredSubscriptions() {
  return readDecoAccountSubscriptions();
}

function cleanFormPayload(form, allowedFields = null) {
  const data = Object.fromEntries(new FormData(form).entries());
  return Object.fromEntries(
    Object.entries(data)
      .filter(([key]) => !allowedFields || allowedFields.includes(key))
      .map(([key, value]) => {
        const clean = String(value || '').trim();
        return [key, clean || null];
      })
  );
}

function normalizeProfile(payload) {
  const source = payload?.cliente || payload?.profile || payload?.user || payload || {};
  return {
    cliente_id: payload?.cliente_id ?? source.cliente_id ?? source.id ?? null,
    nombre: source.nombre ?? source.given_name ?? '',
    apellido: source.apellido ?? source.family_name ?? '',
    email: source.email ?? '',
    telefono: source.telefono ?? '',
    cuil: source.cuil ?? '',
    direccion: source.direccion ?? '',
    direccion_numero: source.direccion_numero ?? '',
    direccion_piso: source.direccion_piso ?? '',
    direccion_dpto: source.direccion_dpto ?? '',
    codigo_postal: source.codigo_postal ?? '',
    ciudad: source.ciudad ?? '',
  };
}

function normalizeNumber(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function normalizeInteger(value) {
  const numeric = Number.parseInt(value, 10);
  return Number.isFinite(numeric) ? numeric : null;
}

function subscriptionQuotePayload(subscription) {
  const items = Array.isArray(subscription?.items)
    ? subscription.items.map((item) => ({
      sku: String(item?.sku || '').trim(),
      qty: normalizeInteger(item?.qty) || 0,
    })).filter((item) => item.sku && item.qty > 0)
    : [];

  const subscriptionType = subscription?.subscription_type === 'indefinite' ? 'indefinite' : 'fixed_term';
  const payload = {
    items,
    subscription_type: subscriptionType,
  };
  if (subscriptionType === 'fixed_term') {
    payload.plazo_meses = normalizeInteger(subscription?.plazo_meses) || 12;
  }
  return payload;
}

async function fetchSubscriptionPaymentUrl(subscription) {
  const existingUrl = normalizePaymentUrl(subscription?.payment_url);
  if (existingUrl) return existingUrl;
  if (subscription?.payment_mode === 'prepaid') return '';

  const payload = subscriptionQuotePayload(subscription);
  if (!payload.items.length) return '';

  const cacheKey = JSON.stringify(payload);
  if (subscriptionPaymentRequests.has(cacheKey)) {
    return subscriptionPaymentRequests.get(cacheKey);
  }

  const request = (async () => {
    for (const endpoint of PACK_QUOTE_ENDPOINTS) {
      try {
        const response = await fetchWithTimeout(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          if ([404, 405].includes(response.status)) continue;
          return '';
        }

        const data = await response.json().catch(() => null);
        const paymentUrl = normalizeQuotePaymentUrl(data);
        return paymentUrl || '';
      } catch (_error) {
        return ''; // No amplificar errores de red o saturación.
      }
    }

    return '';
  })();

  subscriptionPaymentRequests.set(cacheKey, request);
  return request;
}

async function fetchPackQuote(payload) {
  let lastError = null;

  for (const endpoint of PACK_QUOTE_ENDPOINTS) {
    try {
      const response = await fetchWithTimeout(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) return data;

      const detail = data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : detail?.message || data?.message || 'No se pudo recalcular la suscripción.';
      if (!lastError || ![404, 405].includes(response.status)) {
        lastError = new Error(message);
      }
      if (![404, 405].includes(response.status)) throw lastError;
    } catch (error) {
      throw error;
    }
  }

  throw lastError || new Error('No se pudo recalcular la suscripción.');
}

function normalizePackChangeRequests(payload) {
  const requests = Array.isArray(payload?.requests) ? payload.requests : [];
  return requests.map((request) => ({
    request_id: String(request?.request_id || ''),
    reservation_id: String(request?.reservation_id || ''),
    status: String(request?.status || '').trim().toUpperCase(),
    current_amount: normalizeNumber(request?.current_amount),
    requested_amount: normalizeNumber(request?.requested_amount),
    currency: String(request?.currency || 'ARS'),
    effective_at: request?.effective_at || '',
    requested_at: request?.requested_at || '',
    decided_at: request?.decided_at || '',
    applied_at: request?.applied_at || '',
    message: String(request?.message || ''),
  })).filter((request) => request.request_id && request.reservation_id);
}

function normalizeSubscriptions(payload) {
  const source = payload?.subscriptions || payload?.suscripciones || [];
  if (!Array.isArray(source)) return [];

  return source.map((subscription) => ({
    reservation_id: String(subscription?.reservation_id || ''),
    estado: String(subscription?.estado || ''),
    fecha: subscription?.fecha || '',
    notas: subscription?.notas || '',
    items: Array.isArray(subscription?.items)
      ? subscription.items.map((item) => ({
        sku: String(item?.sku || ''),
        qty: normalizeInteger(item?.qty) || 0,
        nombre: item?.nombre || '',
        mueble: item?.mueble || '',
        calidad: item?.calidad || '',
        image_url: item?.image_url || item?.image || '',
        puntaje_unitario: normalizeInteger(item?.puntaje_unitario),
        puntaje_subtotal: normalizeInteger(item?.puntaje_subtotal),
      })).filter((item) => item.sku && item.qty > 0)
      : [],
    puntaje_total: normalizeInteger(subscription?.puntaje_total),
    pack: subscription?.pack || null,
    subscription_type: subscription?.subscription_type === 'indefinite' ? 'indefinite' : 'fixed_term',
    plazo_meses: normalizeInteger(subscription?.plazo_meses),
    mes_actual: normalizeInteger(subscription?.mes_actual),
    mes_total: normalizeInteger(subscription?.mes_total),
    proximo_pago: subscription?.proximo_pago || '',
    fecha_fin: subscription?.fecha_fin || '',
    descuento_pct: normalizeNumber(subscription?.descuento_pct),
    valor_base: normalizeNumber(subscription?.valor_base),
    valor_final: normalizeNumber(subscription?.valor_final),
    payment_mode: subscription?.payment_mode === 'prepaid' ? 'prepaid' : 'monthly',
    total_a_pagar: normalizeNumber(subscription?.total_a_pagar),
    subtotal_periodo: normalizeNumber(subscription?.subtotal_periodo),
    descuento_anticipado_pct: normalizeNumber(subscription?.descuento_anticipado_pct),
    currency: String(subscription?.currency || 'ARS'),
    payment_url: normalizePaymentUrl(
      subscription?.payment_url
      || subscription?.mp_checkout_url
      || subscription?.mp_url
      || subscription?.mp_url_used
      || subscription?.checkout_url
    ),
    billing_status: String(subscription?.billing_status || '').trim().toUpperCase(),
    provider_status: String(subscription?.provider_status || '').trim().toLowerCase(),
    quote_error: subscription?.quote_error || '',
  })).filter((subscription) => subscription.reservation_id);
}

function storeAccountPayload(payload) {
  const profile = normalizeProfile(payload);
  const subscriptions = normalizeSubscriptions(payload);
  writeDecoAccountSession({ profile, subscriptions });
  return { profile, subscriptions };
}

function normalizeTermsStatus(payload) {
  const terms = payload?.terms || payload || {};
  return {
    pending: terms.pending === true,
    current: terms.current || null,
    accepted: terms.accepted || null,
  };
}

function getStoredSessionToken() {
  return getDecoAccountSessionToken();
}

function getAuthHeaders() {
  const token = getStoredSessionToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function resolveApiUrl(apiBaseUrl, path) {
  const base = String(apiBaseUrl || '/apps/deco-account').trim().replace(/\/+$/, '');
  return `${base}${path}`;
}

function injectTermsBase(html, assetsBaseUrl) {
  const baseTag = `<base href="${escapeHtml(assetsBaseUrl)}"><style>body.deco-terms-document-body{margin:0;padding:24px;color:#1f2933;background:#fff;font-family:Arial,sans-serif;line-height:1.5;}img{max-width:100%;height:auto;}table{max-width:100%;}</style>`;
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head([^>]*)>/i, `<head$1>${baseTag}`);
  }
  return `<!doctype html><html><head>${baseTag}</head><body>${html}</body></html>`;
}

function createTermsSrcdoc(html, assetsBaseUrl) {
  const content = injectTermsBase(html || '<p>No hay contenido disponible.</p>', assetsBaseUrl);
  return content.replace(/<body([^>]*)>/i, '<body$1 class="deco-terms-document-body">');
}

function getFilenameFromDisposition(disposition, fallback) {
  const value = String(disposition || '');
  const utfMatch = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch) return decodeURIComponent(utfMatch[1]);
  const plainMatch = value.match(/filename="?([^";]+)"?/i);
  return plainMatch ? plainMatch[1] : fallback;
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function termsRequest(apiBaseUrl, path, options = {}) {
  const useCredentials = !/^https?:\/\//i.test(String(apiBaseUrl || '').trim());
  return fetchWithTimeout(resolveApiUrl(apiBaseUrl, path), {
    credentials: useCredentials ? 'include' : 'omit',
    ...options,
    headers: {
      Accept: 'application/json',
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...getAuthHeaders(),
      ...(options.headers || {}),
    },
  });
}

function parseTermsError(payload, fallback) {
  return payload?.message || payload?.detail || fallback;
}

let activeTermsModalPromise = null;

async function ensureTermsAccepted(apiBaseUrl) {
  if (activeTermsModalPromise) {
    return activeTermsModalPromise;
  }

  activeTermsModalPromise = (async () => {
    const response = await termsRequest(apiBaseUrl, '/terms/current');
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(parseTermsError(data, 'No se pudieron cargar los términos.'));
    }

    if (data.pending !== true || !data.current?.id) {
      return normalizeTermsStatus(data);
    }

    return showTermsModal({
      apiBaseUrl,
      termsVersion: data.current,
      contentHtml: data.content_html || '',
    });
  })();

  try {
    return await activeTermsModalPromise;
  } finally {
    activeTermsModalPromise = null;
  }
}

function showTermsModal({ apiBaseUrl, termsVersion, contentHtml }) {
  const existing = document.getElementById(TERMS_MODAL_ID);
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.id = TERMS_MODAL_ID;
  modal.className = 'deco-terms-modal';
  modal.setAttribute('role', 'presentation');

  const assetsBaseUrl = resolveApiUrl(apiBaseUrl, `/terms/assets/${termsVersion.id}/`);
  modal.innerHTML = `
    <div class="deco-terms-modal__backdrop"></div>
    <section class="deco-terms-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="deco-terms-modal-title">
      <header class="deco-terms-modal__header">
        <div>
          <p class="deco-terms-modal__eyebrow">Términos y condiciones</p>
          <h2 id="deco-terms-modal-title">${escapeHtml(termsVersion.titulo || 'Términos y condiciones')}</h2>
          <p class="deco-terms-modal__meta">Versión ${escapeHtml(termsVersion.version || '')}</p>
        </div>
      </header>
      <div class="deco-terms-modal__viewer">
        <iframe class="deco-terms-modal__iframe" title="Términos y condiciones" sandbox="allow-same-origin"></iframe>
      </div>
      <p class="deco-terms-modal__hint" data-deco-terms-hint>Al aceptar confirmás que leíste y aceptás los términos y condiciones.</p>
      <p class="deco-terms-modal__error" data-deco-terms-error hidden></p>
      <footer class="deco-terms-modal__footer">
        <button class="button deco-terms-modal__accept" type="button" data-deco-terms-accept>Aceptar</button>
      </footer>
    </section>
  `;

  document.body.appendChild(modal);
  document.body.classList.add('deco-terms-modal-open');

  const iframe = modal.querySelector('.deco-terms-modal__iframe');
  const acceptButton = modal.querySelector('[data-deco-terms-accept]');
  const errorEl = modal.querySelector('[data-deco-terms-error]');

  if (iframe instanceof HTMLIFrameElement) {
    iframe.srcdoc = createTermsSrcdoc(contentHtml, assetsBaseUrl);
  }

  return new Promise((resolve) => {
    acceptButton?.addEventListener('click', async () => {
      if (!(acceptButton instanceof HTMLButtonElement)) return;
      acceptButton.disabled = true;
      acceptButton.textContent = 'Aceptando...';
      if (errorEl instanceof HTMLElement) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }

      try {
        const response = await termsRequest(apiBaseUrl, '/terms/accept', {
          method: 'POST',
          body: JSON.stringify({ terms_version_id: termsVersion.id }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(parseTermsError(payload, 'No se pudo registrar la aceptación.'));
        }
        modal.remove();
        document.body.classList.remove('deco-terms-modal-open');
        resolve(normalizeTermsStatus(payload));
      } catch (error) {
        acceptButton.disabled = false;
        acceptButton.textContent = 'Aceptar';
        if (errorEl instanceof HTMLElement) {
          errorEl.textContent = error instanceof Error ? error.message : 'No se pudo registrar la aceptación.';
          errorEl.hidden = false;
        }
      }
    });
  });
}

window.DecoTerms = window.DecoTerms || {};
window.DecoTerms.ensureAccepted = ensureTermsAccepted;

function safeReturnTo(value) {
  if (!value) return '';

  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return '';
    return `${url.pathname}${url.search}${url.hash}`;
  } catch (_error) {
    return '';
  }
}

function profileTabFromLocation() {
  const params = new URLSearchParams(window.location.search);
  const tabFromQuery = String(params.get('tab') || '').trim().toLowerCase();
  const tabFromHash = String(window.location.hash || '').replace(/^#/, '').trim().toLowerCase();
  if (PROFILE_TABS.includes(tabFromQuery)) return tabFromQuery;
  if (PROFILE_TABS.includes(tabFromHash)) return tabFromHash;
  return '';
}

class DecoAccount extends HTMLElement {
  connectedCallback() {
    this.instanceId = this.instanceId || ++decoAccountInstanceCount;
    this.view = this.dataset.view || 'popover';
    this.googleClientId = (this.dataset.googleClientId || '').trim();
    this.apiBaseUrl = (this.dataset.apiBaseUrl || '/apps/deco-account').trim().replace(/\/+$/, '');
    this.profilePath = (this.dataset.profilePath || '/pages/mi-cuenta').trim();
    this.returnTo = this.view === 'page'
      ? safeReturnTo(new URLSearchParams(window.location.search).get('return_to'))
      : '';
    this.afterCompleteAction = '';
    this.profile = this.hasSessionToken() ? readStoredProfile() : null;
    this.subscriptions = this.hasSessionToken() ? normalizeSubscriptions({ subscriptions: readStoredSubscriptions() }) : [];
    this.packChangeRequests = [];
    this.packRulesConfig = null;
    this.packEditor = null;
    this.packEditorLoadingReservationId = '';
    this.packQuoteSequence = 0;
    this.googleProfile = null;
    this.googleCredential = '';
    this.termsStatus = null;
    this.activeProfileTab = this.activeProfileTab || (this.view === 'page' ? profileTabFromLocation() : '');
    this.status = this.view === 'page' && this.hasSessionToken() ? 'loading' : (this.profile ? 'profile' : 'signedOut');
    this.message = '';
    this.error = '';

    this.handleAccountPanelClosed = () => {
      this.afterCompleteAction = '';
    };
    this.handlePopoverToggle = (event) => {
      if (event.newState === 'closed') {
        this.handleAccountPanelClosed();
      }
    };
    this.handleReturnToEvent = (event) => {
      const returnTo = safeReturnTo(event.detail?.returnTo || '');
      const afterComplete = event.detail?.afterComplete === 'open-cart-drawer' ? 'open-cart-drawer' : '';
      if (returnTo) {
        this.returnTo = returnTo;
      }
      if (afterComplete) {
        this.afterCompleteAction = afterComplete;
        this.returnTo = '';
      }

      if (this.profile && this.missingContactFields(this.profile).length > 0) {
        this.status = this.nextStatusForProfile();
        this.render();
      }
    };
    this.handleAccountChanged = () => {
      this.profile = readStoredProfile();
      this.subscriptions = normalizeSubscriptions({ subscriptions: readStoredSubscriptions() });
      if (this.status !== 'completeProfile') {
        this.status = this.profile ? this.nextStatusForProfile() : 'signedOut';
        this.render();
      }
    };
    this.handleGoogleCredentialEvent = (event) => {
      this.handleGoogleCredential(event.detail?.credential || '');
    };

    window.addEventListener('deco-account:return-to', this.handleReturnToEvent);
    window.addEventListener(DECO_ACCOUNT_CHANGED_EVENT, this.handleAccountChanged);
    window.addEventListener('deco-account:google-credential', this.handleGoogleCredentialEvent);
    this.accountPopover = this.closest('[popover]');
    this.accountDialog = this.closest('dialog');
    this.accountPopover?.addEventListener('toggle', this.handlePopoverToggle);
    this.accountDialog?.addEventListener('close', this.handleAccountPanelClosed);
    this.render();

    if (this.view === 'page' && this.hasSessionToken()) {
      this.loadProfile();
    }
  }

  disconnectedCallback() {
    window.removeEventListener('deco-account:return-to', this.handleReturnToEvent);
    window.removeEventListener(DECO_ACCOUNT_CHANGED_EVENT, this.handleAccountChanged);
    window.removeEventListener('deco-account:google-credential', this.handleGoogleCredentialEvent);
    this.accountPopover?.removeEventListener('toggle', this.handlePopoverToggle);
    this.accountDialog?.removeEventListener('close', this.handleAccountPanelClosed);
    this.closePackPicker();
    this.closePackChangeConfirmation();
  }

  endpoint(path) {
    return `${this.apiBaseUrl}${path}`;
  }

  authHeaders() {
    const token = getDecoAccountSessionToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  hasSessionToken() {
    return hasDecoAccountSession();
  }

  async request(path, options = {}) {
    const useCredentials = !/^https?:\/\//i.test(this.apiBaseUrl);
    const response = await fetchWithTimeout(this.endpoint(path), {
      credentials: useCredentials ? 'include' : 'omit',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...this.authHeaders(),
        ...(options.headers || {}),
      },
    });

    const text = await response.text();
    let payload = {};
    if (text) {
      try {
        payload = JSON.parse(text);
      } catch (_error) {
        payload = { detail: text };
      }
    }

    if (!response.ok) {
      const message = payload?.message
        || (typeof payload?.detail === 'string' ? payload.detail : '')
        || (response.status === 409 ? 'Ya existe una cuenta con ese email/CUIL' : '')
        || 'No se pudo completar la operacion.';
      const error = new Error(message);
      error.status = response.status;
      throw error;
    }

    return payload;
  }

  async refreshPackChangeRequests() {
    if (!this.hasSessionToken()) {
      this.packChangeRequests = [];
      return;
    }

    try {
      const data = await this.request('/pack-change-requests');
      this.packChangeRequests = normalizePackChangeRequests(data);
    } catch (error) {
      console.warn('[deco-account] No se pudieron cargar las solicitudes de cambio.', error);
    }
  }

  async renderGoogleButton() {
    const target = this.querySelector('[data-deco-google-button]');
    if (!target) return;

    if (!this.googleClientId) {
      target.innerHTML = '<div class="deco-account__error">Falta configurar Google Client ID.</div>';
      return;
    }

    try {
      await loadGoogleScript();
      initializeGoogle(this.googleClientId);
      target.innerHTML = '';
      window.google.accounts.id.renderButton(target, {
        locale: 'es',
        shape: 'rectangular',
        size: 'large',
        text: 'signin_with',
        theme: 'outline',
        width: this.view === 'page' ? 360 : 320,
      });
    } catch (error) {
      target.innerHTML = `<div class="deco-account__error">${escapeHtml(error.message)}</div>`;
    }
  }

  async handleGoogleCredential(credential) {
    if (!credential) {
      this.error = 'Google no devolvio credenciales.';
      this.render();
      return;
    }

    try {
      const profile = decodeJwtPayload(credential);
      if (profile.email_verified !== true) {
        this.error = 'Google no confirmo el email de esta cuenta.';
        this.render();
        return;
      }

      this.googleCredential = credential;
      this.googleProfile = profile;
      this.status = 'loading';
      this.error = '';
      this.message = '';
      this.render();
      await this.registerGoogleSession(credential);
    } catch (_error) {
      this.error = 'No se pudo leer la respuesta de Google.';
      this.status = 'signedOut';
      this.render();
    }
  }

  async registerGoogleSession(credential) {
    try {
      const data = await this.request('/google/register', {
        method: 'POST',
        body: JSON.stringify({
          credential,
          id_token: credential,
        }),
      });
      this.saveSession(data);

      const profileData = await this.request('/me');
      const stored = storeAccountPayload(profileData);
      this.profile = stored.profile;
      this.subscriptions = stored.subscriptions;
      this.termsStatus = normalizeTermsStatus(profileData);
      await this.refreshPackChangeRequests();
      this.googleCredential = '';
      this.googleProfile = null;
      this.status = this.nextStatusForProfile();
      this.error = '';
      dispatchDecoAccountChanged({ reason: 'profile-loaded' });
      if (this.redirectIfComplete()) return;
      this.render();
      this.promptForPendingTerms();
    } catch (error) {
      this.error = error.message;
      this.status = 'signedOut';
      this.render();
    }
  }

  saveSession(payload) {
    const token = payload?.session_token || payload?.token || '';
    writeDecoAccountSession({ token });

    const stored = storeAccountPayload(payload);
    this.profile = stored.profile;
    this.subscriptions = stored.subscriptions;
    this.termsStatus = normalizeTermsStatus(payload);
    this.googleCredential = '';
    this.googleProfile = null;
    dispatchDecoAccountChanged({ reason: 'session-saved' });
  }

  clearSession() {
    clearDecoAccountSession({ reason: 'session-cleared' });
    this.profile = null;
    this.subscriptions = [];
    this.packChangeRequests = [];
    this.packEditor = null;
    this.packRulesConfig = null;
    this.closePackChangeConfirmation();
    this.termsStatus = null;
    this.googleCredential = '';
    this.googleProfile = null;
    this.afterCompleteAction = '';
    this.activeProfileTab = '';
  }

  async register(form) {
    this.error = '';
    this.message = '';

    try {
      const credential = this.googleCredential;
      if (!credential && this.hasSessionToken()) {
        await this.updateProfile(form);
        return;
      }

      const payloadFields = cleanFormPayload(form);
      this.setBusy(form, true);

      if (!credential) {
        throw new Error('Volver a iniciar sesion con Google.');
      }

      const payload = {
        credential,
        id_token: credential,
        ...payloadFields,
      };

      const data = await this.request('/google/register', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      this.saveSession(data);
      this.message = 'Datos guardados.';
      await this.loadProfile({ silent: true, successMessage: 'Datos guardados.' });
    } catch (error) {
      this.error = error.message;
      this.render();
    }
  }

  async loadProfile({ silent = false, successMessage = '' } = {}) {
    if (!this.hasSessionToken()) {
      this.clearSession();
      this.status = 'signedOut';
      this.render();
      return;
    }

    if (!silent) {
      this.status = 'loading';
      this.render();
    }

    try {
      const data = await this.request('/me');
      const stored = storeAccountPayload(data);
      this.profile = stored.profile;
      this.subscriptions = stored.subscriptions;
      this.termsStatus = normalizeTermsStatus(data);
      await this.refreshPackChangeRequests();
      this.status = this.nextStatusForProfile();
      this.error = '';
      if (successMessage) {
        this.message = successMessage;
      }
      dispatchDecoAccountChanged({ reason: 'profile-loaded' });
      if (this.redirectIfComplete()) return;
      this.render();
      this.promptForPendingTerms();
    } catch (error) {
      if (error.status === 401 || error.status === 403) {
        this.clearSession();
        this.status = 'signedOut';
      } else {
        this.error = error.message;
      }
      this.render();
    }
  }

  async updateProfile(form) {
    this.error = '';
    this.message = '';
    const payloadFields = cleanFormPayload(form, CONTACT_FIELDS);
    this.setBusy(form, true);

    try {
      const data = await this.request('/me', {
        method: 'PATCH',
        body: JSON.stringify(payloadFields),
      });
      const stored = storeAccountPayload(data);
      this.profile = stored.profile;
      this.subscriptions = stored.subscriptions;
      this.termsStatus = normalizeTermsStatus(data);
      this.message = 'Datos actualizados.';
      this.status = this.nextStatusForProfile();
      dispatchDecoAccountChanged({ reason: 'profile-updated' });
      if (this.redirectIfComplete()) return;
      this.render();
      this.promptForPendingTerms();
    } catch (error) {
      this.error = error.message;
      this.render();
    }
  }

  async logout() {
    try {
      await this.request('/logout', { method: 'POST' });
    } catch (_error) {
      // El endpoint puede no existir si la sesion vive solo en el cliente.
    }

    this.clearSession();
    this.status = 'signedOut';
    this.message = '';
    this.error = '';
    this.render();
  }

  async promptForPendingTerms() {
    if (!this.hasSessionToken() || this.termsStatus?.pending !== true) return;

    try {
      this.termsStatus = await ensureTermsAccepted(this.apiBaseUrl);
      const data = await this.request('/me');
      const stored = storeAccountPayload(data);
      this.profile = stored.profile;
      this.subscriptions = stored.subscriptions;
      this.termsStatus = normalizeTermsStatus(data);
      this.status = this.nextStatusForProfile();
      dispatchDecoAccountChanged({ reason: 'terms-accepted' });
      if (this.redirectIfComplete()) return;
      this.render();
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'No se pudieron cargar los términos.';
      this.render();
    }
  }

  async downloadAcceptedTerms(button) {
    if (!this.hasSessionToken()) return;
    const originalLabel = button?.textContent || '';
    if (button) {
      button.disabled = true;
      button.textContent = 'Preparando...';
    }

    try {
      const response = await fetchWithTimeout(this.endpoint('/terms/accepted/download'), {
        credentials: /^https?:\/\//i.test(this.apiBaseUrl) ? 'omit' : 'include',
        headers: {
          ...this.authHeaders(),
        },
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.detail || 'No se pudo descargar el documento.');
      }
      const blob = await response.blob();
      const filename = getFilenameFromDisposition(
        response.headers.get('content-disposition'),
        'terminos-y-condiciones.pdf'
      );
      triggerBlobDownload(blob, filename);
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'No se pudo descargar el documento.';
      this.render();
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = originalLabel;
      }
    }
  }

  setBusy(form, busy) {
    form.querySelectorAll('button, input').forEach((element) => {
      element.disabled = busy;
    });
  }

  packChangeForReservation(reservationId, { openOnly = false } = {}) {
    return this.packChangeRequests.find((request) => (
      request.reservation_id === reservationId
      && (!openOnly || PACK_CHANGE_OPEN_STATUSES.has(request.status))
    )) || null;
  }

  packChangeStatusInfo(request) {
    const statuses = {
      PENDING: ['Pendiente de aprobación', 'deco-account__badge--warn'],
      APPROVED: ['Aprobada', 'deco-account__badge--ok'],
      APPLYING: ['Aplicando cambio', 'deco-account__badge--warn'],
      APPLIED: ['Cambio aplicado', 'deco-account__badge--ok'],
      REJECTED: ['Solicitud rechazada', 'deco-account__badge--danger'],
      REVIEW_REQUIRED: ['Requiere revisión', 'deco-account__badge--warn'],
      RECONCILIATION_REQUIRED: ['Requiere conciliación', 'deco-account__badge--warn'],
    };
    const [label, className] = statuses[request?.status] || ['En revisión', 'deco-account__badge--warn'];
    return { label, className };
  }

  subscriptionCanRequestChange(subscription) {
    if (subscription.payment_mode === 'prepaid') return false;
    if (this.subscriptionStatusInfo(subscription).kind !== 'active') return false;
    if (this.packChangeForReservation(subscription.reservation_id, { openOnly: true })) return false;
    return subscription.billing_status === 'ACTIVE' || subscription.provider_status === 'authorized';
  }

  packItemsSignature(items) {
    return (items || [])
      .map((item) => [normalizeSku(item.sku), Number(item.qty || 0)])
      .filter(([sku, qty]) => sku && qty > 0)
      .sort(([skuA], [skuB]) => skuA.localeCompare(skuB))
      .map(([sku, qty]) => `${sku}:${qty}`)
      .join('|');
  }

  packProductForSku(sku) {
    return this.packRulesConfig?.productsBySku?.get(normalizeSku(sku)) || null;
  }

  normalizeEditorItem(item) {
    const product = this.packProductForSku(item?.sku);
    return {
      sku: normalizeSku(item?.sku),
      qty: Number.parseInt(item?.qty, 10) || 0,
      nombre: product?.nombre || item?.nombre || item?.sku || '',
      mueble: product?.mueble || item?.mueble || '',
      image_url: product?.imageUrl || item?.image_url || '',
    };
  }

  async ensurePackEditor(reservationId) {
    if (this.packEditor?.reservationId === reservationId) return this.packEditor;

    const subscription = this.subscriptions.find((entry) => entry.reservation_id === reservationId);
    if (!subscription || !this.subscriptionCanRequestChange(subscription)) {
      throw new Error('Esta suscripción no admite una nueva solicitud de cambio.');
    }

    this.packEditorLoadingReservationId = reservationId;
    this.error = '';
    this.render();
    try {
      this.packRulesConfig = this.packRulesConfig || await getPackRulesConfig();
      const items = subscription.items.map((item) => this.normalizeEditorItem(item));
      this.packEditor = {
        reservationId,
        subscription,
        originalSignature: this.packItemsSignature(items),
        items,
        quote: null,
        quoteError: '',
        busy: false,
        dirty: false,
      };
      return this.packEditor;
    } finally {
      this.packEditorLoadingReservationId = '';
      this.render();
    }
  }

  packEditorQuotePayload(editor) {
    return subscriptionQuotePayload({
      ...editor.subscription,
      items: editor.items,
    });
  }

  async recalculatePackEditor() {
    const editor = this.packEditor;
    if (!editor) return;

    const validation = validatePackSelection(editor.items, this.packRulesConfig);
    editor.dirty = this.packItemsSignature(editor.items) !== editor.originalSignature;
    editor.quote = null;
    editor.quoteError = validation.valid
      ? ''
      : validation.violations.map((violation) => getViolationMessage(violation)).join(' ');

    if (!editor.dirty || editor.quoteError) {
      this.render();
      return;
    }

    const sequence = ++this.packQuoteSequence;
    editor.busy = true;
    this.render();
    try {
      const quote = await fetchPackQuote(this.packEditorQuotePayload(editor));
      if (sequence !== this.packQuoteSequence || this.packEditor !== editor) return;
      editor.quote = quote;
      editor.items = editor.items.map((item) => this.normalizeEditorItem(item));
      editor.quoteError = '';
    } catch (error) {
      if (sequence !== this.packQuoteSequence || this.packEditor !== editor) return;
      editor.quoteError = error instanceof Error ? error.message : 'No se pudo recalcular la suscripción.';
    } finally {
      if (sequence === this.packQuoteSequence && this.packEditor === editor) {
        editor.busy = false;
        this.render();
      }
    }
  }

  async removePackItem(reservationId, sku) {
    try {
      const editor = await this.ensurePackEditor(reservationId);
      this.error = '';
      const nextItems = editor.items.filter((item) => normalizeSku(item.sku) !== normalizeSku(sku));
      if (!nextItems.length) {
        this.error = 'El pack debe conservar al menos un mueble.';
        this.render();
        return;
      }
      editor.items = nextItems;
      await this.recalculatePackEditor();
      this.openPackChangeConfirmation();
    } catch (error) {
      this.error = error.message;
      this.render();
    }
  }

  async replacePackItem(reservationId, currentSku, replacementSku) {
    try {
      const editor = await this.ensurePackEditor(reservationId);
      this.error = '';
      const current = editor.items.find((item) => normalizeSku(item.sku) === normalizeSku(currentSku));
      const product = this.packProductForSku(replacementSku);
      if (!current || !product) throw new Error('No se pudo identificar el mueble elegido.');

      const existing = editor.items.find((item) => normalizeSku(item.sku) === product.sku);
      editor.items = editor.items
        .filter((item) => normalizeSku(item.sku) !== normalizeSku(currentSku))
        .map((item) => existing && normalizeSku(item.sku) === product.sku
          ? this.normalizeEditorItem({ ...item, qty: item.qty + current.qty })
          : item);
      if (!existing) {
        editor.items.push(this.normalizeEditorItem({ ...product, qty: current.qty }));
      }
      this.closePackPicker();
      await this.recalculatePackEditor();
      this.openPackChangeConfirmation();
    } catch (error) {
      this.closePackPicker();
      this.error = error.message;
      this.render();
    }
  }

  async addPackItem(reservationId, sku) {
    try {
      const editor = await this.ensurePackEditor(reservationId);
      this.error = '';
      const product = this.packProductForSku(sku);
      if (!product) throw new Error('No se pudo identificar el mueble elegido.');
      const decision = getPackAddDecision({
        sku: product.sku,
        requestedQuantity: 1,
        cartItems: editor.items,
        config: this.packRulesConfig,
        canAdjustQuantity: true,
      });
      if (!decision.allowed) throw new Error(decision.message || 'Este mueble no se puede agregar al pack.');

      editor.items.push(this.normalizeEditorItem({ ...product, qty: decision.quantity }));
      this.closePackPicker();
      await this.recalculatePackEditor();
      this.openPackChangeConfirmation();
    } catch (error) {
      this.closePackPicker();
      this.error = error.message;
      this.render();
    }
  }

  cancelPackEditor() {
    this.packQuoteSequence += 1;
    this.packEditor = null;
    this.closePackPicker();
    this.closePackChangeConfirmation();
    this.render();
  }

  openPackChangeConfirmation() {
    const editor = this.packEditor;
    if (!editor || !editor.dirty) return;

    this.closePackChangeConfirmation();
    const currentAmount = this.formatMoney(editor.subscription.valor_final, editor.subscription.currency);
    const requestedAmount = this.formatMoney(
      editor.quote?.valor_final,
      editor.quote?.currency || editor.subscription.currency
    );
    const modal = document.createElement('dialog');
    modal.id = PACK_CHANGE_CONFIRM_MODAL_ID;
    modal.className = 'deco-pack-confirm';
    modal.setAttribute('aria-labelledby', 'deco-pack-confirm-title');
    modal.innerHTML = `
      <div class="deco-pack-confirm__header">
        <div>
          <span class="deco-pack-confirm__eyebrow">Solicitud de cambio</span>
          <h2 id="deco-pack-confirm-title">Confirmá el nuevo valor de tu suscripción</h2>
        </div>
        <button class="deco-pack-confirm__close" type="button" aria-label="Cerrar" title="Cerrar" data-deco-pack-confirm-cancel>×</button>
      </div>
      <div class="deco-pack-confirm__body">
        <div class="deco-pack-confirm__values">
          <span>Valor actual<strong>${escapeHtml(currentAmount || 'No disponible')}</strong></span>
          <span>Nuevo valor<strong data-deco-pack-confirm-amount>${escapeHtml(requestedAmount || 'Pendiente')}</strong></span>
        </div>
        <p class="deco-pack-confirm__note">
          La solicitud será revisada antes de aplicarse. Si se aprueba, el nuevo valor regirá desde la próxima cuota, sin prorrateos anteriores.
        </p>
        <p class="deco-pack-confirm__error" role="alert" aria-live="polite" data-deco-pack-confirm-error hidden></p>
      </div>
      <div class="deco-pack-confirm__footer">
        <button class="deco-pack-confirm__cancel" type="button" data-deco-pack-confirm-cancel>Cancelar</button>
        <button class="deco-pack-confirm__submit" type="button" data-deco-pack-confirm-submit>Enviar solicitud</button>
      </div>
    `;

    const cancel = () => {
      if (this.packEditor?.busy) return;
      this.cancelPackEditor();
    };
    modal.querySelectorAll('[data-deco-pack-confirm-cancel]').forEach((button) => {
      button.addEventListener('click', cancel);
    });
    modal.querySelector('[data-deco-pack-confirm-submit]')?.addEventListener('click', () => this.submitPackChange());
    modal.addEventListener('cancel', (event) => {
      event.preventDefault();
      cancel();
    });
    document.body.appendChild(modal);
    document.body.classList.add('deco-pack-confirm-open');
    if (typeof modal.showModal === 'function') {
      modal.showModal();
    } else {
      modal.setAttribute('open', '');
    }
    this.updatePackChangeConfirmation();
    const submit = modal.querySelector('[data-deco-pack-confirm-submit]');
    const cancelButton = modal.querySelector('.deco-pack-confirm__cancel');
    (submit?.disabled ? cancelButton : submit)?.focus();
  }

  updatePackChangeConfirmation() {
    const modal = document.getElementById(PACK_CHANGE_CONFIRM_MODAL_ID);
    const editor = this.packEditor;
    if (!(modal instanceof HTMLElement) || !editor) return;

    const requestedAmount = this.formatMoney(
      editor.quote?.valor_final,
      editor.quote?.currency || editor.subscription.currency
    );
    const amount = modal.querySelector('[data-deco-pack-confirm-amount]');
    const error = modal.querySelector('[data-deco-pack-confirm-error]');
    const submit = modal.querySelector('[data-deco-pack-confirm-submit]');
    const cancelButtons = modal.querySelectorAll('[data-deco-pack-confirm-cancel]');

    if (amount) amount.textContent = requestedAmount || 'Pendiente';
    if (error) {
      error.textContent = editor.quoteError || '';
      error.hidden = !editor.quoteError;
    }
    if (submit instanceof HTMLButtonElement) {
      submit.disabled = !editor.dirty || !editor.quote || editor.busy;
      submit.textContent = editor.busy ? 'Enviando...' : 'Enviar solicitud';
    }
    cancelButtons.forEach((button) => {
      if (button instanceof HTMLButtonElement) button.disabled = editor.busy;
    });
    modal.setAttribute('aria-busy', editor.busy ? 'true' : 'false');
  }

  closePackChangeConfirmation() {
    const modal = document.getElementById(PACK_CHANGE_CONFIRM_MODAL_ID);
    if (modal?.open && typeof modal.close === 'function') modal.close();
    modal?.remove();
    document.body.classList.remove('deco-pack-confirm-open');
  }

  async submitPackChange() {
    const editor = this.packEditor;
    if (!editor || !editor.dirty || !editor.quote || editor.busy) return;

    editor.busy = true;
    editor.quoteError = '';
    this.updatePackChangeConfirmation();
    try {
      const request = await this.request('/pack-change-requests', {
        method: 'POST',
        body: JSON.stringify({
          reservation_id: editor.reservationId,
          items: editor.items.map((item) => ({ sku: item.sku, qty: item.qty })),
        }),
      });
      this.packChangeRequests = [
        ...normalizePackChangeRequests({ requests: [request] }),
        ...this.packChangeRequests,
      ];
      this.packEditor = null;
      this.closePackChangeConfirmation();
      this.message = request.message || 'Solicitud de cambio enviada para aprobación.';
      this.error = '';
      this.render();
    } catch (error) {
      editor.busy = false;
      editor.quoteError = error.message;
      this.updatePackChangeConfirmation();
    }
  }

  async openPackPicker(reservationId, { mode, currentSku = '' }) {
    try {
      await this.ensurePackEditor(reservationId);
      const currentMuebleKey = currentSku ? mapSkuToFurniture(currentSku, this.packRulesConfig) : '';
      const categories = Array.from(this.packRulesConfig.productsByMueble.entries())
        .filter(([, products]) => products.some((product) => product.available))
        .map(([key, products]) => ({ key, label: products[0]?.mueble || key }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
      const selectedCategory = mode === 'change' ? currentMuebleKey : categories[0]?.key;
      if (!selectedCategory) throw new Error('No hay muebles disponibles para seleccionar.');

      this.closePackPicker();
      const modal = document.createElement('dialog');
      modal.id = PACK_PICKER_MODAL_ID;
      modal.className = 'deco-pack-picker';
      modal.setAttribute('aria-labelledby', 'deco-pack-picker-title');
      modal.dataset.mode = mode;
      modal.dataset.reservationId = reservationId;
      modal.dataset.currentSku = currentSku;
      modal.dataset.category = selectedCategory;
      modal.innerHTML = `
        <div class="deco-pack-picker__header">
          <div>
            <span class="deco-pack-picker__eyebrow">${mode === 'change' ? 'Cambiar mueble' : 'Agregar mueble'}</span>
            <h2 id="deco-pack-picker-title">${mode === 'change' ? 'Elegí otra opción de la misma categoría' : 'Elegí un mueble'}</h2>
          </div>
          <button class="deco-pack-picker__close" type="button" aria-label="Cerrar" title="Cerrar" data-deco-pack-picker-close>×</button>
        </div>
        ${mode === 'add' ? `
          <label class="deco-pack-picker__category">
            <span>Categoría</span>
            <select data-deco-pack-picker-category>
              ${categories.map((category) => `
                <option value="${escapeHtml(category.key)}" ${category.key === selectedCategory ? 'selected' : ''}>${escapeHtml(category.label)}</option>
              `).join('')}
            </select>
          </label>
        ` : ''}
        <div class="deco-pack-picker__products" data-deco-pack-picker-products></div>
      `;
      document.body.appendChild(modal);
      document.body.classList.add('deco-pack-picker-open');
      modal.querySelector('[data-deco-pack-picker-close]')?.addEventListener('click', () => this.closePackPicker());
      modal.querySelector('[data-deco-pack-picker-category]')?.addEventListener('change', (event) => {
        modal.dataset.category = event.currentTarget.value;
        this.renderPackPickerProducts(modal);
      });
      modal.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closePackPicker();
      });
      this.renderPackPickerProducts(modal);
      if (typeof modal.showModal === 'function') {
        modal.showModal();
      } else {
        modal.setAttribute('open', '');
      }
      modal.querySelector('[data-deco-pack-product]')?.focus();
    } catch (error) {
      this.error = error.message;
      this.render();
    }
  }

  renderPackPickerProducts(modal) {
    const container = modal.querySelector('[data-deco-pack-picker-products]');
    if (!container) return;
    const products = (this.packRulesConfig?.productsByMueble?.get(modal.dataset.category) || [])
      .filter((product) => product.available);
    const currentSku = normalizeSku(modal.dataset.currentSku);

    if (!products.length) {
      container.innerHTML = '<p class="deco-pack-picker__empty">No hay muebles disponibles en esta categoría.</p>';
      return;
    }

    container.innerHTML = products.map((product) => {
      const selected = product.sku === currentSku;
      const alreadyIncluded = modal.dataset.mode === 'add' && this.packEditor?.items.some(
        (item) => normalizeSku(item.sku) === product.sku
      );
      const disabled = selected || alreadyIncluded;
      const imageUrl = normalizeImageUrl(product.imageUrl);
      return `
        <button
          class="deco-pack-picker__product ${disabled ? 'is-current' : ''}"
          type="button"
          data-deco-pack-product
          data-sku="${escapeHtml(product.sku)}"
          ${disabled ? 'disabled' : ''}
        >
          <span class="deco-pack-picker__image">
            ${imageUrl
              ? `<img src="${escapeHtml(imageUrl)}" alt="" loading="lazy" width="160" height="120">`
              : `<span aria-hidden="true">${escapeHtml(product.nombre.charAt(0).toUpperCase() || 'D')}</span>`}
          </span>
          <span class="deco-pack-picker__product-copy">
            <strong>${escapeHtml(product.nombre)}</strong>
            <small>${selected ? 'Actual' : alreadyIncluded ? 'Ya incluido' : escapeHtml(product.sku)}</small>
          </span>
        </button>
      `;
    }).join('');

    container.querySelectorAll('[data-deco-pack-product]:not(:disabled)').forEach((button) => {
      button.addEventListener('click', () => {
        if (modal.dataset.mode === 'change') {
          this.replacePackItem(modal.dataset.reservationId, modal.dataset.currentSku, button.dataset.sku);
        } else {
          this.addPackItem(modal.dataset.reservationId, button.dataset.sku);
        }
      });
    });
  }

  closePackPicker() {
    const modal = document.getElementById(PACK_PICKER_MODAL_ID);
    if (modal?.open && typeof modal.close === 'function') modal.close();
    modal?.remove();
    document.body.classList.remove('deco-pack-picker-open');
  }

  nextStatusForProfile(profile = this.profile) {
    if (!profile) return 'signedOut';
    if (this.termsStatus?.pending === true) return 'profile';
    return this.missingContactFields(profile).length > 0 ? 'completeProfile' : 'profile';
  }

  render() {
    if (this.status === 'loading') {
      this.innerHTML = this.view === 'page'
        ? '<div class="deco-account__page deco-account__loading"><div class="deco-account__status">Cargando cuenta...</div></div>'
        : '<div class="deco-account__status">Cargando cuenta...</div>';
      return;
    }

    if (this.status === 'completeProfile') {
      this.renderCompletionForm();
      return;
    }

    if (this.status === 'profile' && this.profile) {
      this.view === 'page' ? this.renderProfileForm() : this.renderProfileSummary();
      return;
    }

    this.renderSignedOut();
  }

  renderSignedOut() {
    if (this.view === 'page') {
      this.innerHTML = `
        <div class="deco-account__page deco-account__signed-out">
          <div class="deco-account__panel deco-account__panel--intro">
            <span class="deco-account__eyebrow">Acceso seguro</span>
            <h2 class="deco-account__heading">Inicia sesion para ver tus datos</h2>
            ${this.error ? `<div class="deco-account__error">${escapeHtml(this.error)}</div>` : ''}
            <div class="deco-account__google" data-deco-google-wrap>
              <div data-deco-google-button></div>
            </div>
          </div>
        </div>
      `;
    } else {
      this.innerHTML = `
        <div class="deco-account__google">
          ${this.error ? `<div class="deco-account__error">${escapeHtml(this.error)}</div>` : ''}
          <div data-deco-google-button></div>
        </div>
      `;
    }
    this.renderGoogleButton();
  }

  renderCompletionForm() {
    const googleProfile = this.googleProfile || {};
    const profile = this.profile || {};
    const fullName = [
      googleProfile.given_name || profile.nombre,
      googleProfile.family_name || profile.apellido,
    ].filter(Boolean).join(' ');
    const email = googleProfile.email || profile.email || '';

    this.innerHTML = `
      <form class="deco-account__form ${this.view === 'page' ? 'deco-account__page' : ''}" data-deco-complete-form>
        <div class="deco-account__panel deco-account__panel--identity">
          <div class="deco-account__identity">
            <span class="deco-account__eyebrow">Cuenta Google verificada</span>
            <span class="deco-account__name">${escapeHtml(fullName || email || 'Cuenta Google')}</span>
            <span class="deco-account__email">${escapeHtml(email)}</span>
          </div>
        </div>
        ${this.error ? `<div class="deco-account__error">${escapeHtml(this.error)}</div>` : ''}
        <div class="deco-account__panel">
          <div class="deco-account__section-head">
            <h2 class="deco-account__section-title">Datos de contacto</h2>
          </div>
          <div class="deco-account__fields">
            ${this.fieldMarkup('telefono', 'Telefono', profile.telefono || '', 'tel')}
            ${this.fieldMarkup('cuil', 'CUIL', profile.cuil || '')}
            ${this.fieldMarkup('direccion', 'Calle', profile.direccion || '')}
            ${this.fieldMarkup('direccion_numero', 'Numero', profile.direccion_numero || '')}
            ${this.fieldMarkup('direccion_piso', 'Piso', profile.direccion_piso || '')}
            ${this.fieldMarkup('direccion_dpto', 'Dpto.', profile.direccion_dpto || '')}
            ${this.fieldMarkup('codigo_postal', 'Codigo postal', profile.codigo_postal || '')}
            ${this.fieldMarkup('ciudad', 'Ciudad', profile.ciudad || '')}
          </div>
        </div>
        <div class="deco-account__actions">
          <button class="button" type="submit">Guardar datos</button>
        </div>
      </form>
    `;

    this.querySelector('[data-deco-complete-form]')?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.register(event.currentTarget);
    });
  }

  renderProfileSummary() {
    const profile = this.profile;
    const fullName = [profile.nombre, profile.apellido].filter(Boolean).join(' ') || 'Mi cuenta';
    const missingCount = this.missingContactFields(profile).length;
    const subscriptions = Array.isArray(this.subscriptions) ? this.subscriptions : [];
    const subscriptionsCount = subscriptions.length;
    const subscriptionsStatus = subscriptionsCount ? this.subscriptionsStatusInfo(subscriptions) : null;
    const subscriptionsText = subscriptionsStatus
      ? `Suscripción ${subscriptionsStatus.label.toLowerCase()}`
      : '';

    this.innerHTML = `
      <div class="deco-account__profile">
        ${this.message ? `<div class="deco-account__success">${escapeHtml(this.message)}</div>` : ''}
        ${this.error ? `<div class="deco-account__error">${escapeHtml(this.error)}</div>` : ''}
        <div class="deco-account__identity">
          <span class="deco-account__name">${escapeHtml(fullName)}</span>
          <span class="deco-account__email">${escapeHtml(profile.email || '')}</span>
          <span class="deco-account__meta">${profile.cliente_id ? `Cliente #${escapeHtml(profile.cliente_id)} - ` : ''}${missingCount ? `${missingCount} dato${missingCount === 1 ? '' : 's'} pendiente${missingCount === 1 ? '' : 's'}` : 'Datos completos'}</span>
          ${subscriptionsText ? `<span class="deco-account__meta">${escapeHtml(subscriptionsText)}</span>` : ''}
        </div>
        <div class="deco-account__actions">
          <a class="button-secondary deco-account__link" href="${escapeHtml(this.profilePath)}">Mis datos</a>
          ${this.termsStatus?.pending === true ? '<button class="button-secondary" type="button" data-deco-accept-terms>Aceptar términos</button>' : ''}
          <button class="button-secondary" type="button" data-deco-logout>Salir</button>
        </div>
      </div>
    `;

    this.querySelector('[data-deco-logout]')?.addEventListener('click', () => this.logout());
    this.querySelector('[data-deco-accept-terms]')?.addEventListener('click', () => this.promptForPendingTerms());
  }

  renderProfileForm() {
    const profile = this.profile;
    const fullName = [profile.nombre, profile.apellido].filter(Boolean).join(' ') || 'Mi cuenta';
    const missingCount = this.missingContactFields(profile).length;
    const statusText = missingCount
      ? `${missingCount} dato${missingCount === 1 ? '' : 's'} pendiente${missingCount === 1 ? '' : 's'}`
      : 'Datos completos';
    const acceptedTerms = this.termsStatus?.accepted || null;
    const pendingTerms = this.termsStatus?.pending === true;
    const termsVersionLabel = acceptedTerms?.version
      ? `Versión ${acceptedTerms.version}`
      : (this.termsStatus?.current?.version ? `Versión ${this.termsStatus.current.version}` : 'Sin versión aceptada');
    const activeTab = PROFILE_TABS.includes(this.activeProfileTab)
      ? this.activeProfileTab
      : (pendingTerms ? 'terminos' : (missingCount ? 'personales' : 'usuario'));
    if (pendingTerms) {
      this.activeProfileTab = 'terminos';
    }
    const resolvedActiveTab = pendingTerms ? 'terminos' : activeTab;
    this.activeProfileTab = resolvedActiveTab;

    this.innerHTML = `
      <form class="deco-account__form deco-account__page" data-deco-profile-form>
        <div class="deco-account__hero">
          <div class="deco-account__avatar" aria-hidden="true">${escapeHtml(this.initials(profile))}</div>
          <div class="deco-account__identity">
            <span class="deco-account__eyebrow">Cuenta Deco Rental</span>
            <span class="deco-account__name">${escapeHtml(fullName)}</span>
            <span class="deco-account__email">${escapeHtml(profile.email || '')}</span>
          </div>
          <div class="deco-account__badges">
            <span class="deco-account__badge deco-account__badge--ok">Google verificado</span>
            <span class="deco-account__badge ${missingCount ? 'deco-account__badge--warn' : 'deco-account__badge--ok'}">${escapeHtml(statusText)}</span>
          </div>
        </div>
        ${this.message ? `<div class="deco-account__success">${escapeHtml(this.message)}</div>` : ''}
        ${this.error ? `<div class="deco-account__error">${escapeHtml(this.error)}</div>` : ''}
        ${this.profileTabsMarkup(resolvedActiveTab)}
        <div class="deco-account__tab-panels">
          ${this.profileTabPanel('usuario', resolvedActiveTab, `
            <section class="deco-account__panel">
              <div class="deco-account__section-head">
                <h2 class="deco-account__section-title">Cuenta e identidad</h2>
                ${profile.cliente_id ? `<span class="deco-account__client-id">Cliente #${escapeHtml(profile.cliente_id)}</span>` : ''}
              </div>
              <div class="deco-account__identity">
                <span class="deco-account__meta">Cuenta Google verificada</span>
                <span class="deco-account__meta">${escapeHtml(statusText)}</span>
              </div>
              <div class="deco-account__fields deco-account__fields--readonly">
                ${this.fieldMarkup('nombre', 'Nombre', profile.nombre, 'text', true)}
                ${this.fieldMarkup('apellido', 'Apellido', profile.apellido, 'text', true)}
                ${this.fieldMarkup('email', 'Email', profile.email, 'email', true)}
              </div>
            </section>
          `)}
          ${this.profileTabPanel('personales', resolvedActiveTab, `
            <section class="deco-account__panel">
              <div class="deco-account__section-head">
                <h2 class="deco-account__section-title">Contacto y envío</h2>
              </div>
              <div class="deco-account__fields">
                ${this.fieldMarkup('telefono', 'Telefono', profile.telefono, 'tel')}
                ${this.fieldMarkup('cuil', 'CUIL', profile.cuil)}
                ${this.fieldMarkup('direccion', 'Calle', profile.direccion)}
                ${this.fieldMarkup('direccion_numero', 'Numero', profile.direccion_numero)}
                ${this.fieldMarkup('direccion_piso', 'Piso', profile.direccion_piso)}
                ${this.fieldMarkup('direccion_dpto', 'Dpto.', profile.direccion_dpto)}
                ${this.fieldMarkup('codigo_postal', 'Codigo postal', profile.codigo_postal)}
                ${this.fieldMarkup('ciudad', 'Ciudad', profile.ciudad)}
              </div>
              <div class="deco-account__actions deco-account__actions--compact">
                <button class="button" type="submit">Guardar cambios</button>
              </div>
            </section>
          `)}
          ${this.profileTabPanel('suscripciones', resolvedActiveTab, this.renderSubscriptionsPanel())}
          ${this.profileTabPanel('terminos', resolvedActiveTab, this.renderTermsPanel(acceptedTerms, pendingTerms, termsVersionLabel))}
        </div>
        <div class="deco-account__actions deco-account__actions--compact">
          <button class="button-secondary" type="button" data-deco-logout>Salir</button>
        </div>
      </form>
    `;

    this.querySelectorAll('[data-deco-profile-tab]').forEach((button) => {
      button.addEventListener('click', () => this.setProfileTab(button.dataset.decoProfileTab || 'usuario'));
    });
    this.querySelector('[data-deco-profile-form]')?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.updateProfile(event.currentTarget);
    });
    this.querySelector('[data-deco-logout]')?.addEventListener('click', () => this.logout());
    this.querySelector('[data-deco-download-terms]')?.addEventListener('click', (event) => {
      this.downloadAcceptedTerms(event.currentTarget);
    });
    this.querySelector('[data-deco-accept-terms]')?.addEventListener('click', () => this.promptForPendingTerms());
    this.querySelectorAll('[data-deco-pack-change]').forEach((button) => {
      button.addEventListener('click', () => this.openPackPicker(button.dataset.reservationId, {
        mode: 'change',
        currentSku: button.dataset.sku,
      }));
    });
    this.querySelectorAll('[data-deco-pack-remove]').forEach((button) => {
      button.addEventListener('click', () => this.removePackItem(button.dataset.reservationId, button.dataset.sku));
    });
    this.querySelectorAll('[data-deco-pack-add]').forEach((button) => {
      button.addEventListener('click', () => this.openPackPicker(button.dataset.reservationId, { mode: 'add' }));
    });
    if (resolvedActiveTab === 'suscripciones') {
      this.refreshSubscriptionImages();
      this.refreshSubscriptionPaymentLinks();
    }
  }

  profileTabId(tabName) {
    return `deco-account-${this.instanceId}-tab-${tabName}`;
  }

  profilePanelId(tabName) {
    return `deco-account-${this.instanceId}-panel-${tabName}`;
  }

  profileTabsMarkup(activeTab) {
    const tabs = [
      ['usuario', 'Usuario'],
      ['personales', 'Personales'],
      ['suscripciones', 'Suscripciones'],
      ['terminos', 'Términos'],
    ];

    return `
      <div class="deco-account__tabs" role="tablist" aria-label="Mi cuenta">
        ${tabs.map(([tabName, label]) => {
          const selected = activeTab === tabName;
          return `
            <button
              class="deco-account__tab ${selected ? 'is-active' : ''}"
              type="button"
              role="tab"
              id="${this.profileTabId(tabName)}"
              aria-controls="${this.profilePanelId(tabName)}"
              aria-selected="${selected ? 'true' : 'false'}"
              data-deco-profile-tab="${tabName}"
            >${escapeHtml(label)}</button>
          `;
        }).join('')}
      </div>
    `;
  }

  profileTabPanel(tabName, activeTab, content) {
    const selected = activeTab === tabName;
    return `
      <section
        class="deco-account__tab-panel ${selected ? 'is-active' : ''}"
        role="tabpanel"
        id="${this.profilePanelId(tabName)}"
        aria-labelledby="${this.profileTabId(tabName)}"
        data-deco-profile-panel="${tabName}"
        ${selected ? '' : 'hidden'}
      >
        ${content}
      </section>
    `;
  }

  setProfileTab(tabName) {
    if (!PROFILE_TABS.includes(tabName)) return;

    this.activeProfileTab = tabName;
    this.querySelectorAll('[data-deco-profile-tab]').forEach((button) => {
      const selected = button.dataset.decoProfileTab === tabName;
      button.classList.toggle('is-active', selected);
      button.setAttribute('aria-selected', selected ? 'true' : 'false');
    });
    this.querySelectorAll('[data-deco-profile-panel]').forEach((panel) => {
      const selected = panel.dataset.decoProfilePanel === tabName;
      panel.classList.toggle('is-active', selected);
      panel.hidden = !selected;
    });
    if (tabName === 'suscripciones') {
      this.refreshSubscriptionImages();
      this.refreshSubscriptionPaymentLinks();
    }
  }

  renderTermsPanel(acceptedTerms, pendingTerms, termsVersionLabel) {
    return `
      <section class="deco-account__panel">
        <div class="deco-account__section-head">
          <h2 class="deco-account__section-title">Términos y condiciones</h2>
          <span class="deco-account__badge ${pendingTerms ? 'deco-account__badge--warn' : acceptedTerms ? 'deco-account__badge--ok' : ''}">${pendingTerms ? 'Aceptación pendiente' : acceptedTerms ? 'Términos aceptados' : 'Sin aceptación'}</span>
        </div>
        <div class="deco-account__identity">
          <span class="deco-account__meta">${escapeHtml(termsVersionLabel)}</span>
          ${acceptedTerms?.accepted_at ? `<span class="deco-account__meta">Aceptados el ${escapeHtml(new Date(acceptedTerms.accepted_at).toLocaleDateString('es-AR'))}</span>` : ''}
        </div>
        <div class="deco-account__actions deco-account__actions--compact">
          ${acceptedTerms ? '<button class="button-secondary" type="button" data-deco-download-terms>Descargar términos aceptados</button>' : ''}
          ${pendingTerms ? '<button class="button-secondary" type="button" data-deco-accept-terms>Aceptar términos</button>' : ''}
        </div>
      </section>
    `;
  }

  renderSubscriptionsPanel() {
    const subscriptions = Array.isArray(this.subscriptions) ? this.subscriptions : [];
    if (!subscriptions.length) {
      return `
        <section class="deco-account__panel">
          <div class="deco-account__section-head">
            <h2 class="deco-account__section-title">Suscripción</h2>
            <span class="deco-account__badge">Sin suscripción</span>
          </div>
          <span class="deco-account__meta">No hay suscripciones activas para esta cuenta.</span>
        </section>
      `;
    }

    const statusInfo = this.subscriptionsStatusInfo(subscriptions);
    const pendingNote = statusInfo.label === 'Pendiente'
      ? 'Luego del pago podemos demorarnos hasta 2hs en confirmar la reserva'
      : '';
    return `
      <section class="deco-account__panel">
        <div class="deco-account__section-head">
          <h2 class="deco-account__section-title">Suscripción</h2>
          <span class="deco-account__badge-stack">
            <span class="deco-account__badge ${escapeHtml(statusInfo.className)}">${escapeHtml(statusInfo.label)}</span>
            ${pendingNote ? `<span class="deco-account__meta deco-account__pending-note">${escapeHtml(pendingNote)}</span>` : ''}
          </span>
        </div>
        <div class="deco-account__subscriptions">
          ${subscriptions.map((subscription) => this.subscriptionMarkup(subscription)).join('')}
        </div>
      </section>
    `;
  }

  subscriptionsStatusInfo(subscriptions) {
    if (subscriptions.some(subscription => this.subscriptionStatusInfo(subscription).kind === 'review')) {
      return { label: 'Pago en revisión', className: 'deco-account__badge--danger' };
    }
    const hasPending = subscriptions.some((subscription) => this.subscriptionStatusInfo(subscription).kind === 'pending');
    if (hasPending) return { label: 'Pendiente', className: 'deco-account__badge--danger' };
    return { label: 'Activa', className: 'deco-account__badge--ok' };
  }

  subscriptionStatusInfo(subscription) {
    if (subscription?.billing_status === 'REVIEW_REQUIRED') {
      return { kind: 'review', label: 'Pago en revisión', className: 'deco-account__badge--danger' };
    }
    const estado = String(subscription?.estado || '').trim().toUpperCase();
    if (estado === 'BORRADOR') {
      return { kind: 'pending', label: 'Pendiente', className: 'deco-account__badge--danger' };
    }
    if (estado === 'CREADA' || estado === 'CONFIRMADA') {
      return { kind: 'active', label: 'Activa', className: 'deco-account__badge--ok' };
    }
    return { kind: 'pending', label: 'Pendiente', className: 'deco-account__badge--danger' };
  }

  subscriptionMarkup(subscription) {
    const statusInfo = this.subscriptionStatusInfo(subscription);
    const isPrepaid = subscription.payment_mode === 'prepaid';
    const valueLabel = this.formatMoney(isPrepaid ? subscription.total_a_pagar : subscription.valor_final, subscription.currency);
    const dateLabel = this.formatDate(subscription.fecha);
    const isIndefinite = subscription.subscription_type === 'indefinite';
    const monthLabel = subscription.mes_actual
      ? (subscription.mes_total ? `${subscription.mes_actual}/${subscription.mes_total}` : `Ciclo ${subscription.mes_actual}`)
      : '';
    const nextPaymentLabel = isPrepaid ? '' : this.formatDate(subscription.proximo_pago);
    const endDateLabel = this.formatDate(subscription.fecha_fin);
    const paymentUrl = statusInfo.kind === 'pending' ? normalizePaymentUrl(subscription.payment_url) : '';
    const changeRequest = this.packChangeForReservation(subscription.reservation_id);
    const openChangeRequest = this.packChangeForReservation(subscription.reservation_id, { openOnly: true });
    const isEditing = this.packEditor?.reservationId === subscription.reservation_id;
    const displayItems = isEditing ? this.packEditor.items : subscription.items;
    const canRequestChange = this.subscriptionCanRequestChange(subscription);
    const showItemActions = canRequestChange || isEditing;
    const editorLoading = this.packEditorLoadingReservationId === subscription.reservation_id;
    const details = [
      ['Id reserva', subscription.reservation_id],
      ['Fecha', dateLabel],
      ['Mes', monthLabel],
      ['Próximo pago', nextPaymentLabel],
      ['Fin suscripción', endDateLabel],
      ['Modalidad', isIndefinite ? 'Mensual flexible' : 'Por plazo'],
      ['Plazo', !isIndefinite && subscription.plazo_meses ? `${subscription.plazo_meses} meses` : ''],
      ['Forma de pago', isPrepaid ? 'Anticipado · pago único' : 'Mensualmente'],
      [isPrepaid ? 'Total anticipado' : 'Valor mensual', valueLabel],
      ['Descuento por plazo', subscription.descuento_pct ? `${subscription.descuento_pct}%` : ''],
      ['Descuento anticipado', isPrepaid ? `${subscription.descuento_anticipado_pct || 15}% adicional` : ''],
    ].filter((detail) => detail[1]);

    return `
      <article class="deco-account__subscription">
        <div class="deco-account__subscription-head">
          <div class="deco-account__identity">
            <span class="deco-account__name">Reserva</span>
            <span class="deco-account__meta">Detalle de la suscripción</span>
          </div>
          ${statusInfo.kind === 'pending' ? `
            <div
              class="deco-account__subscription-actions"
              data-deco-subscription-actions
              data-reservation-id="${escapeHtml(subscription.reservation_id)}"
            >
              ${paymentUrl ? this.subscriptionPayLinkMarkup(paymentUrl) : ''}
            </div>
          ` : ''}
        </div>
        <div class="deco-account__subscription-details">
          ${details.map(([label, value]) => `
            <div class="deco-account__subscription-detail">
              <span>${escapeHtml(label)}</span>
              <strong>${escapeHtml(value)}</strong>
            </div>
          `).join('')}
        </div>
        ${isPrepaid ? `<p class="deco-account__meta">${statusInfo.kind === 'pending' ? 'El período se confirma cuando Mercado Pago apruebe el pago.' : statusInfo.kind === 'review' ? 'Contactá a Deco Rental para revisar el pago.' : 'Período abonado anticipadamente. Sin próximos cobros mensuales automáticos.'}</p>` : ''}
        ${changeRequest ? this.packChangeRequestMarkup(changeRequest) : ''}
        ${canRequestChange || isEditing ? `
          <div class="deco-account__pack-toolbar">
            <div class="deco-account__identity">
              <strong>Composición del pack</strong>
              <span class="deco-account__meta">Los cambios requieren aprobación y rigen desde la próxima cuota.</span>
            </div>
          </div>
        ` : ''}
        <div class="deco-account__subscription-items-wrap">
          <table class="deco-account__subscription-items">
            <thead>
              <tr>
                <th scope="col">Foto</th>
                <th scope="col">Producto</th>
                <th scope="col">Cantidad</th>
                ${showItemActions ? '<th scope="col"><span class="visually-hidden">Acciones</span></th>' : ''}
              </tr>
            </thead>
            <tbody>
              ${displayItems.map((item) => this.subscriptionItemMarkup(item, {
                reservationId: subscription.reservation_id,
                showActions: showItemActions,
                disabled: editorLoading || this.packEditor?.busy,
              })).join('')}
            </tbody>
          </table>
        </div>
        ${canRequestChange || isEditing ? `
          <div class="deco-account__pack-add-row">
            <button
              class="deco-account__pack-add"
              type="button"
              data-deco-pack-add
              data-reservation-id="${escapeHtml(subscription.reservation_id)}"
              ${editorLoading || this.packEditor?.busy ? 'disabled' : ''}
            >${editorLoading ? 'Cargando...' : 'Agregar muebles'}</button>
          </div>
        ` : ''}
        ${!canRequestChange && !openChangeRequest && statusInfo.kind === 'active' && !isEditing ? `
          <span class="deco-account__meta">Esta suscripción todavía no admite cambios desde la cuenta.</span>
        ` : ''}
        ${subscription.quote_error ? `<span class="deco-account__meta">${escapeHtml(subscription.quote_error)}</span>` : ''}
      </article>
    `;
  }

  packChangeRequestMarkup(request) {
    const status = this.packChangeStatusInfo(request);
    const requestedAmount = this.formatMoney(request.requested_amount, request.currency);
    const effectiveDate = this.formatDate(request.effective_at);
    return `
      <div class="deco-account__pack-request">
        <div class="deco-account__identity">
          <strong>Solicitud de cambio</strong>
          <span class="deco-account__meta">
            ${requestedAmount ? `Nuevo valor: ${escapeHtml(requestedAmount)}.` : ''}
            ${effectiveDate ? ` Vigencia prevista: ${escapeHtml(effectiveDate)}.` : ''}
          </span>
        </div>
        <span class="deco-account__badge ${escapeHtml(status.className)}">${escapeHtml(status.label)}</span>
      </div>
    `;
  }

  subscriptionPayLinkMarkup(paymentUrl) {
    return `
      <a
        class="button-secondary deco-account__subscription-pay"
        href="${escapeHtml(paymentUrl)}"
        target="_blank"
        rel="noopener"
        data-deco-subscription-pay
      >PAGAR</a>
    `;
  }

  subscriptionItemMarkup(item, { reservationId = '', showActions = false, disabled = false } = {}) {
    const itemName = item.nombre || item.sku;
    const imageUrl = normalizeImageUrl(item.image_url);
    const imageMarkup = imageUrl
      ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(itemName)}" loading="lazy" width="72" height="72">`
      : `<span class="deco-account__subscription-image-placeholder" aria-hidden="true">${escapeHtml(String(itemName).charAt(0).toUpperCase() || 'D')}</span>`;

    return `
      <tr class="deco-account__subscription-item">
        <td class="deco-account__subscription-item-photo">
          <span
            class="deco-account__subscription-image"
            data-deco-subscription-image
            data-sku="${escapeHtml(item.sku)}"
            data-name="${escapeHtml(itemName)}"
          >${imageMarkup}</span>
        </td>
        <td>
          <span class="deco-account__subscription-item-name">${escapeHtml(itemName)}</span>
          ${item.mueble ? `<span class="deco-account__subscription-item-category">${escapeHtml(item.mueble)}</span>` : ''}
        </td>
        <td class="deco-account__subscription-item-qty">${escapeHtml(item.qty)}</td>
        ${showActions ? `
          <td class="deco-account__subscription-item-actions">
            <button
              type="button"
              class="deco-account__pack-action"
              data-deco-pack-change
              data-reservation-id="${escapeHtml(reservationId)}"
              data-sku="${escapeHtml(item.sku)}"
              ${disabled ? 'disabled' : ''}
            >Cambiar</button>
            <button
              type="button"
              class="deco-account__pack-action deco-account__pack-action--danger"
              data-deco-pack-remove
              data-reservation-id="${escapeHtml(reservationId)}"
              data-sku="${escapeHtml(item.sku)}"
              ${disabled ? 'disabled' : ''}
            >Quitar</button>
          </td>
        ` : ''}
      </tr>
    `;
  }

  refreshSubscriptionPaymentLinks() {
    const subscriptionsById = new Map(
      (Array.isArray(this.subscriptions) ? this.subscriptions : [])
        .map((subscription) => [String(subscription.reservation_id || ''), subscription])
    );

    this.querySelectorAll('[data-deco-subscription-actions]').forEach((container) => {
      if (!(container instanceof HTMLElement)) return;
      if (container.dataset.paymentLoading === 'true') return;
      if (container.querySelector('[data-deco-subscription-pay]')) return;

      const subscription = subscriptionsById.get(container.dataset.reservationId || '');
      if (!subscription || this.subscriptionStatusInfo(subscription).kind !== 'pending') return;

      container.dataset.paymentLoading = 'true';
      fetchSubscriptionPaymentUrl(subscription).then((paymentUrl) => {
        if (!container.isConnected) return;

        if (paymentUrl) {
          subscription.payment_url = paymentUrl;
          container.innerHTML = this.subscriptionPayLinkMarkup(paymentUrl);
        }
      }).catch((error) => {
        console.warn('[deco-account] No se pudo obtener el link de pago de la suscripcion.', error);
      }).finally(() => {
        delete container.dataset.paymentLoading;
      });
    });
  }

  refreshSubscriptionImages() {
    this.querySelectorAll('[data-deco-subscription-image]').forEach((container) => {
      if (!(container instanceof HTMLElement) || container.dataset.imageLoaded === 'true') return;

      const sku = container.dataset.sku || '';
      const nombre = container.dataset.name || '';
      resolveSubscriptionItemImage({ sku, nombre }).then((imageUrl) => {
        if (!imageUrl || !container.isConnected || container.dataset.imageLoaded === 'true') return;

        container.innerHTML = `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(nombre || sku)}" loading="lazy" width="72" height="72">`;
        container.dataset.imageLoaded = 'true';
      }).catch((_error) => {
        // La imagen es decorativa para este listado; si no se resuelve, queda el placeholder.
      });
    });
  }

  formatDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('es-AR');
  }

  formatMoney(value, currency = 'ARS') {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '';
    try {
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: currency || 'ARS',
        maximumFractionDigits: 0,
      }).format(numeric);
    } catch (_error) {
      return `${currency || 'ARS'} ${numeric.toFixed(0)}`;
    }
  }

  missingContactFields(profile) {
    return REQUIRED_CONTACT_FIELDS.filter((field) => !String(profile?.[field] || '').trim());
  }

  redirectIfComplete() {
    if (!this.profile) return false;
    if (this.missingContactFields(this.profile).length > 0) return false;
    if (this.termsStatus?.pending === true) return false;

    if (this.afterCompleteAction) {
      const action = this.afterCompleteAction;
      this.afterCompleteAction = '';
      this.returnTo = '';
      window.dispatchEvent(new CustomEvent('deco-account:after-complete', {
        detail: { action },
      }));
      return true;
    }

    if (!this.returnTo) return false;

    window.location.href = this.returnTo;
    return true;
  }

  initials(profile) {
    const parts = [profile?.nombre, profile?.apellido].filter(Boolean);
    const source = parts.length ? parts.join(' ') : profile?.email || 'DR';
    return source
      .split(/\s+|@/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || 'DR';
  }

  fieldMarkup(name, label, value = '', type = 'text', readonly = false) {
    const id = `${name}-${Math.random().toString(36).slice(2)}`;
    return `
      <div class="deco-account__field">
        <label for="${id}">${escapeHtml(label)}</label>
        <input
          id="${id}"
          name="${escapeHtml(name)}"
          type="${escapeHtml(type)}"
          value="${escapeHtml(value)}"
          autocomplete="${escapeHtml(FIELD_AUTOCOMPLETE[name] || name)}"
          ${readonly ? 'readonly' : ''}
        >
      </div>
    `;
  }
}

if (!customElements.get('deco-account')) {
  customElements.define('deco-account', DecoAccount);
}
